// Layer 3 (Transformation): the Module 2 metal-accounting layer projected onto
// the plant-model process graph (#163, tim-hook.A3 — Tim: "then layer the
// metal accounting over it"; Miles 2026-08-21 judgement: module home stays at
// /metal-accounting, the layer is graph annotations + a compact gap strip).
// Reads ONLY the existing Module 2 seed — no new figures. Pure — no React.

import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import type {
  AccountingPeriod,
  ChainCheckpoint,
  ChainCheckpointRead,
  ChainGap,
  ChainRead,
  ClosureRead,
  ClosureTone,
  LossDrillRead,
  MeasuredQuantity,
  MeasurementDecode,
  PeriodCloseRow,
  StageLossRow,
  TailsLossRead,
} from '@/versions/v1/schema/metal-accounting';
import {
  ACCOUNTING_REPORT,
  GROSS_ERROR_FLAGS,
  MEASUREMENTS,
  METAL_INVENTORY,
  MINE_CLAIMS,
  PERIOD_CLOSE,
  SHIPMENTS,
} from '@/versions/v1/shared/bunker-hill-accounting-seed';
import { CONCENTRATE_SPECS } from '@/versions/v1/shared/bunker-hill-concentrate-specs';
import { SUBPROCESSES } from '@/versions/v1/shared/bunker-hill-seed';
import { CASES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { daysInMonth, monthStart } from '@/versions/v1/shared/clock.derive';
import { STAGES, type StageId } from '@/versions/v1/modules/overview/StageLadder.transform';
import {
  type ChainElement,
  type EstimateCoverage,
  displayedClosureGapPct,
  estimateContainedFromSeries,
  isPeriodMeasured,
  mtdWindow,
  roundGrade,
  roundMass,
  stageOutcomeValue,
  wtdWindow,
  type PeriodBalance,
  type PeriodView,
} from './periodBalance';

export {
  displayedClosureGapPct,
  isPeriodMeasured,
  periodView,
  periodWindows,
  type PeriodBalance,
  type PeriodView,
} from './periodBalance';

const WIT021_CASE = {
  caseId: 'case-wit-021',
  drawerTitle: 'WIT-021 sole fresh-feed anchor',
};

const GROSS_ERROR_CHIP_LABEL = 'single anchor — no cross-check';

const MEASUREMENT_NODE_IDS: Partial<Record<string, readonly string[]>> = {
  'sp-crush': ['ms-wit021'],
  'sp-grind': ['ms-fit203', 'ms-dt203', 'rv-circload'],
  'sp-zn': ['ms-sa04'],
  'sp-pb': ['ms-sa03'],
};

export type MaAnnotation = {
  text: string;
  /** Second line for in-circuit contained-metal breakdown (vision.V10 #314). */
  subText?: string;
  tone: VizTone;
  title: string;
};

export type NodeGapChip = {
  pct: string;
  tone: ClosureTone;
  /** Decode for the chip — DESIGN.md § *Decided* keeps the legend on glass by
   *  tooltip where the mark is dense. */
  title: string;
};

export type MaClosureCell = {
  id: string;
  label: string;
  value: string;
  sub: string;
  tone: VizTone;
  /** #275: an independent tone for `sub`, when `sub` carries a different
   *  quantity than `value` (e.g. a loss-fraction headline with a tails-grade
   *  sub-value toned against the FMEA spec). `undefined` renders `sub` in
   *  the neutral muted style, same as before this field existed. */
  subTone?: VizTone;
  title: string;
  /** paul-fmea.A12 / #277: set only on the Zn/Pb tails-loss cell — which
   *  element's loss drill this cell opens. `undefined` everywhere else,
   *  including the Ag tails-loss cell (no drill affordance for Ag). */
  drillElement?: 'Zn' | 'Pb';
};

export type MaClosureStripView = {
  cells: readonly MaClosureCell[];
  caption: string;
  provenance: string;
  provenanceChip: string;
};

export type MetalAccountingLayerView = {
  nodeNotes: Record<string, MaAnnotation>;
  nodeGaps: Record<string, NodeGapChip | null>;
  edgeLabels: Record<string, readonly MaAnnotation[]>;
  strip: MaClosureStripView;
};

/** On-glass placeholder when a read is intentionally absent — never the word unmeasured. */
const GLASS_NO_READING = '—';

/** Shared gap label — one formatter for chip, strip, and Balance tab. K17
 *  round 1 SHOULD: it reads `displayedGapPct`, so the label and the threshold
 *  band that tests it can never disagree on a rounding boundary. */
export function formatGapPct(gapPct: number): string {
  return `${displayedGapPct(gapPct).toFixed(1)} %`;
}

/** Displayed gap precision — the chip, the strip, the Balance tab and the
 *  Close all print one decimal (`formatGapPct`). K17 (#663): the closure band
 *  is evaluated on that same displayed figure, so a gap the glass prints as
 *  `0.5 %` can never be labelled `open` by a threshold test run at a finer
 *  precision than the reader sees. Round half away from zero (K17 round 1
 *  SHOULD), so `−0.55 %` displays `−0.6 %` exactly as `+0.55 %` displays
 *  `+0.6 %` — `Math.round` alone rounds `−5.5` toward `−5`. */
export function displayedGapPct(gapPct: number): number {
  const magnitude = Math.round(Math.abs(gapPct) * 10) / 10;
  const rounded = gapPct < 0 ? -magnitude : magnitude;
  return rounded === 0 ? 0 : rounded;
}

/** Shared closure threshold — KPI, persona pill, table chip, WTD Check
 *  (glass-poc-run A3 D13; K17 #663 one threshold function). |gap| ≤ 0.5 % ok,
 *  ≤ 1.0 % warn, else bad, on the displayed (1 dp) gap. */
export function closureToneFromGap(gapPct: number | null): ClosureTone {
  if (gapPct === null) return 'unmeasured';
  const abs = Math.abs(displayedGapPct(gapPct));
  if (abs <= 0.5) return 'ok';
  if (abs <= 1.0) return 'warn';
  return 'bad';
}

/** The closure Check's verdict — the one function the Check and the tone both
 *  read (K17 #663). `null` gaps are `unmeasured`, never "closes". */
export function closureCloses(gapPct: number | null): boolean {
  return closureToneFromGap(gapPct) === 'ok';
}

const UNMEASURED: MeasuredQuantity = { value: null, unit: 'unmeasured' };

const PERIOD_UNIT_MARKERS = /stpd|t\/d|dst\/d|\/shift/i;

function hasPeriodUnit(unit: string): boolean {
  return PERIOD_UNIT_MARKERS.test(unit);
}

function decimalPlacesFromSeed(value: string): number {
  const match = value.match(/\.(\d+)/);
  return match ? match[1].length : 0;
}

function formatNumber(value: number, decimals?: number): string {
  if (decimals !== undefined) {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

export type FormatMeasuredOptions = {
  /** Explicit fractional digits — graph headlines use 1 (e.g. 83.5 dst Zn). */
  decimals?: number;
  /** Seed text to infer stated precision — balance columns use full seed width (e.g. 83.54). */
  seedText?: string;
};

/** One formatter for chip, strip, Balance tab, and probe — never double-suffixes a period. */
export function formatMeasuredQuantity(
  quantity: MeasuredQuantity,
  _period?: AccountingPeriod,
  options?: FormatMeasuredOptions,
): string {
  if (quantity.value === null) return quantity.unit;
  const unit = quantity.unit.trim();
  const decimals =
    options?.decimals ??
    (options?.seedText ? decimalPlacesFromSeed(options.seedText) : undefined);
  if (!unit) return formatNumber(quantity.value, decimals);
  // Percent-grade quantities carry the '%' inside the unit text itself
  // ('% Zn' from seed strings like '5.44% Zn') — no space before it, matching
  // the seed's own grammar (fix round 1 MR-F5: was rendering '5.4 % Zn').
  if (unit.startsWith('%')) return `${formatNumber(quantity.value, decimals)}${unit}`;
  if (hasPeriodUnit(unit)) return `${formatNumber(quantity.value, decimals)} ${unit}`;
  return `${formatNumber(quantity.value, decimals)} ${unit}`;
}

export function parseSeedQuantity(text: string): MeasuredQuantity {
  const trimmed = text.trim();
  if (!trimmed || trimmed === '—') return UNMEASURED;
  if (trimmed === 'unmeasured') return UNMEASURED;
  if (trimmed.includes('pending equipment')) return { value: null, unit: 'pending equipment data' };
  if (trimmed.includes('does not tie out')) return { value: null, unit: 'path disagreement — flagged' };

  const normalized = trimmed.replace(/^−/, '-');
  const match = normalized.match(/^([+\-]?[\d,.]+)\s*(.*)$/);
  if (!match) return { value: null, unit: trimmed };
  const unit = match[2]?.trim() ?? '';
  return {
    value: Number.parseFloat(match[1].replace(/,/g, '')),
    unit,
  };
}

function gapChip(gapPct: number | null, tone: ClosureTone): NodeGapChip | null {
  if (gapPct === null || tone === 'unmeasured') return null;
  return {
    pct: formatGapPct(gapPct),
    tone,
    // Same wording family as the closure strip and table cells — this is the
    // circuit's mass-balance closure, not a per-stage in/out reading.
    title: 'Mass-balance closure — recovery-derived product rows vs feed metal.',
  };
}

function grossErrorFlagForMeasurement(measurementId: string) {
  return GROSS_ERROR_FLAGS.find((flag) => flag.measurementId === measurementId);
}

/** Node drill-in rows — one measurement per row from `PeriodView` (R8). */
function closureMeasurements(
  view: PeriodView,
  measurementIds: readonly string[],
  nodeId?: string,
): ClosureRead['measurements'] {
  if (!isPeriodMeasured(view) || view.period !== 'day' || ACCOUNTING_REPORT.period !== 'day') {
    return [];
  }
  const balance = view;

  return measurementIds.flatMap((id): ClosureRead['measurements'][number][] => {
    if (id === 'rv-circload') return [];
    if (id === 'ms-wit021') {
      const flag = grossErrorFlagForMeasurement(id);
      return [{
        id: 'ms-wit021',
        label: 'WIT-021',
        measurement: 'Fresh feed',
        value: balance.feed,
        unit: 'dst/d',
        source: 'DCS tag',
        grossErrorFlag: flag
          ? {
              chipLabel: GROSS_ERROR_CHIP_LABEL,
              caseId: WIT021_CASE.caseId,
              drawerTitle: WIT021_CASE.drawerTitle,
            }
          : undefined,
      }];
    }
    if (id === 'ms-sa03' && nodeId === 'sp-pb') {
      return [
        {
          id: 'ms-sa03-mass',
          label: 'SA03',
          measurement: 'Concentrate mass',
          value: roundMass(balance.masses.C1),
          unit: 'dst',
          source: 'LIMS sample',
        },
        {
          id: 'ms-sa03-grade',
          label: 'SA03',
          measurement: 'Pb grade',
          value: roundGrade(balance.productGrade.pbConcPct),
          unit: '% Pb',
          source: 'LIMS sample',
        },
      ];
    }
    if (id === 'ms-sa04' && nodeId === 'sp-zn') {
      return [
        {
          id: 'ms-sa04-mass',
          label: 'SA04',
          measurement: 'Concentrate mass',
          value: roundMass(balance.masses.C2),
          unit: 'dst',
          source: 'LIMS sample',
        },
        {
          id: 'ms-sa04-grade',
          label: 'SA04',
          measurement: 'Zn grade',
          value: roundGrade(balance.productGrade.znConcPct),
          unit: '% Zn',
          source: 'LIMS sample',
        },
      ];
    }
    return [];
  });
}

function signOffForPeriod(): ClosureRead['signOff'] {
  return { state: 'none' };
}

function inventoryChangeForNode(nodeId: string, period: AccountingPeriod): MeasuredQuantity {
  if (period !== 'day' || ACCOUNTING_REPORT.period !== 'day') return UNMEASURED;
  const inventory = METAL_INVENTORY.find((entry) => entry.subprocessId === nodeId);
  if (!inventory) return UNMEASURED;
  if (inventory.status === 'pending-equipment-data') {
    return { value: null, unit: 'pending equipment data' };
  }
  if (inventory.inventoryChangeDelta) {
    return parseSeedQuantity(inventory.inventoryChangeDelta);
  }
  return inventory.holdupEstimate ? parseSeedQuantity(inventory.holdupEstimate) : UNMEASURED;
}

/** Thin view of `PeriodView` for graph chips and Balance tab. */
function circuitRead(nodeId: 'sp-zn' | 'sp-pb', view: PeriodView): ClosureRead | null {
  if (!isPeriodMeasured(view)) return null;

  const element = nodeId === 'sp-zn' ? 'Zn' : 'Pb';
  const gapPct = displayedClosureGapPct(view, element);
  const metalIn = view.metalIn[element];
  const metalOut = view.metalOut[element];

  return {
    nodeId,
    period: view.period,
    element,
    in: { value: metalIn, unit: `dst ${element}` },
    out: { value: metalOut, unit: `dst ${element}` },
    inventoryChange: inventoryChangeForNode(nodeId, view.period),
    closurePct: gapPct,
    tone: closureToneFromGap(gapPct),
    measurements: closureMeasurements(view, MEASUREMENT_NODE_IDS[nodeId] ?? [], nodeId),
    signOff: signOffForPeriod(),
  };
}

function unmeasuredRead(nodeId: string, view: PeriodView): ClosureRead {
  const period = isPeriodMeasured(view) ? view.period : 'shift';
  const inventory = METAL_INVENTORY.find((entry) => entry.subprocessId === nodeId);
  const measurementIds = MEASUREMENT_NODE_IDS[nodeId] ?? [];
  const carriesMetal = nodeId !== 'sp-water' && nodeId !== 'sp-paste';
  const element = carriesMetal
    ? nodeId === 'sp-zn' || inventory?.metal.startsWith('Zn')
      ? 'Zn'
      : nodeId === 'sp-pb' || inventory?.metal.includes('Pb')
        ? 'Pb'
        : 'Pb'
    : undefined;

  return {
    nodeId,
    period,
    ...(element !== undefined ? { element } : {}),
    in: UNMEASURED,
    out: UNMEASURED,
    inventoryChange: inventory ? inventoryChangeForNode(nodeId, period) : UNMEASURED,
    closurePct: null,
    tone: 'unmeasured',
    measurements: closureMeasurements(view, measurementIds, nodeId),
    signOff: signOffForPeriod(),
  };
}

export function closureRead(nodeId: string, view: PeriodView): ClosureRead {
  if (nodeId === 'sp-zn' || nodeId === 'sp-pb') {
    const circuit = circuitRead(nodeId, view);
    if (circuit) return circuit;
    return unmeasuredRead(nodeId, view);
  }

  if (METAL_INVENTORY.some((entry) => entry.subprocessId === nodeId) || MEASUREMENT_NODE_IDS[nodeId]) {
    return unmeasuredRead(nodeId, view);
  }

  return unmeasuredRead(nodeId, view);
}

export function allClosureReads(view: PeriodView): ClosureRead[] {
  return SUBPROCESSES.map((subprocess) => closureRead(subprocess.id, view));
}

// ---------------------------------------------------------------------------
// ma.D1 / #120 (MA4 answered) — mine → mill → shipped reconciliation.
// Mine and shipped are seeded [syn] (MINE_CLAIMS / SHIPMENTS, no Bunker Hill
// source); mill is computed from the existing WIT-021 / ROM-head-assay seed
// already used above for the plant-internal closure read. Pure — no React.
// ---------------------------------------------------------------------------

const CHAIN_UNMEASURED_CHECKPOINT = (
  id: ChainCheckpoint,
  label: string,
  source: string,
  provenance: string,
): ChainCheckpointRead => ({
  id,
  label,
  mass: null,
  grade: null,
  contained: null,
  source,
  provenance,
});

/** Contained metal = mass × grade (Q3) — percent grades (Zn, Pb) divide by
 *  100; oz/t grades (Ag) do not. No least-squares, no other arithmetic. */
function containedFromMassGrade(
  mass: MeasuredQuantity,
  grade: MeasuredQuantity,
  element: ChainElement,
): MeasuredQuantity {
  if (mass.value === null || grade.value === null) return UNMEASURED;
  const isPercent = grade.unit.trim().startsWith('%');
  const value = isPercent ? mass.value * (grade.value / 100) : mass.value * grade.value;
  return { value, unit: isPercent ? `dst ${element}` : `oz ${element}` };
}

function mineCheckpoint(period: AccountingPeriod, element: ChainElement): ChainCheckpointRead {
  const row = MINE_CLAIMS.find((entry) => entry.period === period);
  if (!row) {
    return {
      ...CHAIN_UNMEASURED_CHECKPOINT(
        'mine',
        'Mine',
        'Mine survey',
        '[syn] mine survey claim — no site source',
      ),
      mass: UNMEASURED,
      grade: null,
      contained: UNMEASURED,
    };
  }
  const gradeEntry = row.grades.find((entry) => entry.element === element);
  const mass = parseSeedQuantity(row.tonnesToSurface);
  const grade = gradeEntry ? parseSeedQuantity(gradeEntry.grade) : null;
  const contained = grade ? containedFromMassGrade(mass, grade, element) : UNMEASURED;
  const massUnit = period === 'day' ? 'dst/d' : 'dst';
  const containedUnit = element === 'Ag' ? 'oz Ag' : `dst ${element}`;
  return {
    id: 'mine',
    label: 'Mine',
    mass: mass.value !== null ? { value: mass.value, unit: massUnit } : mass,
    grade,
    contained:
      contained.value !== null ? { value: contained.value, unit: containedUnit } : contained,
    source: row.source,
    provenance: row.provenance,
  };
}

/** Mill checkpoint — feed and head grade from `PeriodView` only (R6). */
function millCheckpoint(view: PeriodView, element: ChainElement): ChainCheckpointRead {
  if (!isPeriodMeasured(view)) {
    return CHAIN_UNMEASURED_CHECKPOINT(
      'mill',
      'Mill',
      'WIT-021 fresh feed × ROM head assay',
      // narration-ok: chain checkpoint tooltip traceability, not chip text
      'A §M2 §6.1',
    );
  }
  const balance = view;
  const period = balance.period;
  const massUnit = period === 'day' ? 'dst/d' : 'dst';
  const mass: MeasuredQuantity = { value: balance.feed, unit: massUnit };
  const grade: MeasuredQuantity = {
    value: balance.headGrade[element],
    unit: element === 'Ag' ? 'oz/t' : '%',
  };
  const contained = containedFromMassGrade(mass, grade, element);
  const periodLabel =
    period === 'day' ? '24 h' : period === 'wtd' ? 'WTD' : period === 'mtd' ? 'MTD' : period;
  return {
    id: 'mill',
    label: 'Mill',
    mass,
    grade,
    contained,
    source: `WIT-021 fresh feed × ROM head assay (${periodLabel})`,
    provenance: 'Derived from periodBalance — feed series × head grade',
  };
}

/** K17 round 3 (#663): Shipped at WTD and MTD is the sum of the despatched
 *  lots dated inside the period's own window — the same `despatchInWindow`
 *  rule the period close uses. It is never the product balance (round 2's WTD
 *  leg read the balance, so Shipped equalled Mill concentrate and the leg
 *  printed a green `0.0 %` between a number and itself). */
function shippedCheckpointFromLots(
  element: ChainElement,
  closeMonth: string,
  window: DespatchWindow,
): ChainCheckpointRead {
  const lots = SHIPMENTS.filter(
    (entry) =>
      entry.closeMonth === closeMonth &&
      despatchInWindow(entry.despatchDate, window) &&
      entry.grades.some((g) => g.element === element),
  );
  if (lots.length === 0) {
    return CHAIN_UNMEASURED_CHECKPOINT(
      'shipped',
      'Shipped',
      'Concentrate despatch record',
      '[syn] concentrate despatch — no site source',
    );
  }
  let totalMass = 0;
  let totalContained = 0;
  for (const lot of lots) {
    const mass = parseSeedQuantity(lot.tonnes);
    const gradeEntry = lot.grades.find((g) => g.element === element);
    if (mass.value === null || !gradeEntry) continue;
    const grade = parseSeedQuantity(gradeEntry.grade);
    const contained = containedFromMassGrade(mass, grade, element);
    totalMass += mass.value ?? 0;
    totalContained += contained.value ?? 0;
  }
  if (totalMass <= 0 || totalContained <= 0) {
    return CHAIN_UNMEASURED_CHECKPOINT('shipped', 'Shipped', 'Concentrate despatch record', lots[0]!.provenance);
  }
  const avgGrade =
    element === 'Ag'
      ? { value: totalContained / totalMass, unit: 'oz/t Ag' }
      : { value: (totalContained / totalMass) * 100, unit: `% ${element}` };
  return {
    id: 'shipped',
    label: 'Shipped',
    mass: { value: totalMass, unit: 'dst' },
    grade: avgGrade,
    contained: { value: totalContained, unit: element === 'Ag' ? 'oz Ag' : `dst ${element}` },
    source: `Despatched concentrate lots inside ${window.label} (invoiced assays)`,
    provenance: lots[0]!.provenance,
  };
}

/** K17 round 1 (#663): the mill's accounted **concentrate** metal — feed ×
 *  recovery, the flowsheet's `77.0 dst Zn` / `35.1 dst Pb` / `~636 oz Ag` at
 *  the day period (product mass × product grade, the same read the flowsheet's
 *  edge labels carry). The mill → shipped leg compares the shipment against
 *  this, never against mill feed metal: a day cannot ship metal the plant never
 *  produced. */
function millConcentrateCheckpoint(view: PeriodView, element: ChainElement): ChainCheckpointRead {
  if (!isPeriodMeasured(view)) {
    return CHAIN_UNMEASURED_CHECKPOINT(
      'mill-concentrate',
      'Mill concentrate',
      'Concentrate produced — product mass × product grade',
      'Derived — PeriodBalance concentrate mass × product grade',
    );
  }
  const balance = view;
  const massUnit = balance.period === 'day' ? 'dst/d' : 'dst';
  const masses = balance.masses;
  const labelled = (source: string): Omit<ChainCheckpointRead, 'mass' | 'grade' | 'contained'> => ({
    id: 'mill-concentrate',
    label: 'Mill concentrate',
    source,
    provenance: 'Derived — PeriodBalance concentrate mass × product grade',
  });
  if (element === 'Zn') {
    const mass: MeasuredQuantity = { value: masses.C2, unit: massUnit };
    const grade: MeasuredQuantity = { value: balance.productGrade.znConcPct, unit: '% Zn' };
    return { ...labelled('Zn concentrate mass × SA04 assay (produced)'), mass, grade, contained: containedFromMassGrade(mass, grade, element) };
  }
  if (element === 'Pb') {
    const mass: MeasuredQuantity = { value: masses.C1, unit: massUnit };
    const grade: MeasuredQuantity = { value: balance.productGrade.pbConcPct, unit: '% Pb' };
    return { ...labelled('Pb/Ag concentrate mass × SA03 Pb assay (produced)'), mass, grade, contained: containedFromMassGrade(mass, grade, element) };
  }
  const mass: MeasuredQuantity = { value: masses.C1, unit: massUnit };
  // F765 (#765): the **reported** product grade is now the assay basis (the strip
  // and /cost read `productGrade.agConcOzPerT`). This checkpoint is the
  // reconciliation's mill → concentrate leg, where the card's own arithmetic is
  // feed × recovery = concentrate, so it reads the balance's **back-calculated**
  // grade — the same figure the closure uses — and the source names that basis
  // rather than the SA03 assay.
  const grade: MeasuredQuantity = { value: balance.closureAgConcOzPerT, unit: 'oz/t Ag' };
  return { ...labelled('Pb/Ag concentrate mass × back-calculated Ag grade (produced)'), mass, grade, contained: containedFromMassGrade(mass, grade, element) };
}

function shippedCheckpoint(view: PeriodView, element: ChainElement): ChainCheckpointRead {
  const period = view.period;
  if (period === 'wtd') {
    // K17 round 3 (#663): the WTD window's own despatch lots — D25's complete
    // production days inside the plant week (2–7 Jun, the six days `/cost`
    // publishes). The open 8 Jun day is in no WTD figure, shipments included.
    const window = wtdDespatchWindow();
    if (!window) {
      return CHAIN_UNMEASURED_CHECKPOINT(
        'shipped',
        'Shipped',
        'Concentrate despatch record',
        '[syn] concentrate despatch — no site source',
      );
    }
    return shippedCheckpointFromLots(element, 'june-2026-mtd', window);
  }
  if (period === 'mtd') {
    // K17 round 2 (#663): only June lots dated inside the close's window count —
    // the same 1–7 Jun window the close and the flowsheet MTD read. A lot dated
    // 8 Jun is despatched after the window and is not in this figure.
    const window = despatchWindowFor('june-2026-mtd');
    if (!window) {
      return CHAIN_UNMEASURED_CHECKPOINT(
        'shipped',
        'Shipped',
        'Concentrate despatch record',
        '[syn] concentrate despatch — no site source',
      );
    }
    return shippedCheckpointFromLots(element, 'june-2026-mtd', window);
  }
  const row = SHIPMENTS.find(
    (entry) => entry.period === period && entry.grades.some((g) => g.element === element),
  );
  if (!row) {
    return CHAIN_UNMEASURED_CHECKPOINT(
      'shipped',
      'Shipped',
      'Concentrate despatch record',
      '[syn] concentrate despatch — no site source',
    );
  }
  const gradeEntry = row.grades.find((entry) => entry.element === element);
  if (!gradeEntry) {
    return CHAIN_UNMEASURED_CHECKPOINT('shipped', 'Shipped', row.source, row.provenance);
  }
  const mass = parseSeedQuantity(row.tonnes);
  const grade = parseSeedQuantity(gradeEntry.grade);
  const contained = containedFromMassGrade(mass, grade, element);
  const massUnit = period === 'day' ? 'dst/d' : 'dst';
  const containedUnit = element === 'Ag' ? 'oz Ag' : `dst ${element}`;
  return {
    id: 'shipped',
    label: 'Shipped',
    mass: mass.value !== null ? { value: mass.value, unit: massUnit } : mass,
    grade,
    contained:
      contained.value !== null ? { value: contained.value, unit: containedUnit } : contained,
    source: row.source,
    provenance: row.provenance,
  };
}

/** K17 round 3 (#663): the mill-concentrate → shipped leg at WTD / MTD is a
 *  **stock position**, not a reconciliation gap. The window's produced metal
 *  and its despatched lots differ by concentrate stock (in the bins or on the
 *  water), never by loss, so the leg prints untoned and carries `pct: null` —
 *  no toned pill, and `worstChainGap` never selects it.
 *
 *  The label is direction-aware: a window that produced more than it shipped
 *  shows what is **not yet despatched** (June to date: `62.0 dst Zn · 11.0 % of
 *  produced`); one that shipped more than it produced shipped **from stock**
 *  built earlier — the WTD week does this, its two lots predating part of the
 *  week's production. Both name the magnitude of the same signed share. */
function stockLegGap(
  produced: ChainCheckpointRead,
  shipped: ChainCheckpointRead,
  element: ChainElement,
): ChainGap {
  const producedValue = produced.contained?.value ?? null;
  const shippedValue = shipped.contained?.value ?? null;
  if (producedValue == null || shippedValue == null || producedValue <= 0) {
    return { from: 'mill-concentrate', to: 'shipped', pct: null, tone: 'unmeasured' };
  }
  const pct = ((shippedValue - producedValue) / producedValue) * 100;
  const notYetDespatched = pct <= 0;
  const mass = formatContained(element, Math.abs(shippedValue - producedValue));
  const share = `${Math.abs(pct).toFixed(1)} % of produced`;
  const stockTitle = notYetDespatched
    ? `Concentrate produced but not yet despatched — ${mass} of the ${formatContained(element, producedValue)} this window produced (${share}). Stock, not loss.`
    : `Concentrate despatched from stock — ${mass} more shipped than this window produced (${share}). Stock, not loss.`;
  return {
    from: 'mill-concentrate',
    to: 'shipped',
    pct: null,
    tone: 'unmeasured',
    stock: {
      kind: notYetDespatched ? 'not-yet-despatched' : 'from-stock',
      label: notYetDespatched ? 'not yet despatched' : 'despatched from stock',
      mass,
      pct,
      share,
      title: stockTitle,
    },
  };
}

function chainGapBetween(
  from: ChainCheckpointRead,
  to: ChainCheckpointRead,
  fromId: ChainCheckpoint,
  toId: ChainCheckpoint,
  period: AccountingPeriod,
  element: ChainElement,
): ChainGap {
  if ((period === 'wtd' || period === 'mtd') && fromId === 'mill-concentrate' && toId === 'shipped') {
    return stockLegGap(from, to, element);
  }
  if (from.contained?.value == null || to.contained?.value == null) {
    return { from: fromId, to: toId, pct: null, tone: 'unmeasured' };
  }
  const pct = ((to.contained.value - from.contained.value) / from.contained.value) * 100;
  return { from: fromId, to: toId, pct, tone: closureToneFromGap(pct) };
}

/** Q3: chainRead(view, element) — pure, no React, no engine mutation.
 *
 *  K17 round 1 (#663): four custody points — Mine · Mill (fresh feed) · Mill
 *  concentrate (feed × recovery) · Shipped — and the two reconciliation legs
 *  the chain names: **mine → mill** (claim vs account, unchanged) and
 *  **mill concentrate → shipped** (produced vs despatched, a stock move). The
 *  mill's own feed → concentrate step is recovery, carried on the closure strip
 *  and the flowsheet, so it is no third gap here.
 *
 *  K17 round 3 (#663): the mill concentrate → shipped leg shows the stock
 *  position at WTD and MTD (`stockLegGap`) and keeps the toned reconciliation
 *  at `day`; `recoveryPct` carries the mill's feed → concentrate number for the
 *  card's recovery arrow. */
export function chainRead(view: PeriodView, element: ChainElement): ChainRead {
  const period = view.period;
  const mine = mineCheckpoint(period, element);
  const mill = millCheckpoint(view, element);
  const millConcentrate = millConcentrateCheckpoint(view, element);
  const shipped = shippedCheckpoint(view, element);
  return {
    period,
    element,
    checkpoints: [mine, mill, millConcentrate, shipped],
    gaps: [
      chainGapBetween(mine, mill, 'mine', 'mill', period, element),
      chainGapBetween(millConcentrate, shipped, 'mill-concentrate', 'shipped', period, element),
    ],
    recoveryPct: recoveryActualPct(element, view),
    note:
      period === 'day'
        ? null
        : period === 'mtd'
          ? 'Mill measured from the ore balance; shipped from despatched lots to date; mine unmeasured — day-only [syn] row.'
          : period === 'wtd'
            ? 'Mill measured from the ore balance; shipped from despatched lots in the week; mine unmeasured — day-only [syn] row.'
            : 'unmeasured',
  };
}

export function allChainReads(view: PeriodView): ChainRead[] {
  return (['Zn', 'Pb', 'Ag'] as const).map((element) => chainRead(view, element));
}

// ---------------------------------------------------------------------------
// paul-fmea.A10 / #275 — tails losses per element. Every figure below is
// DERIVED from the closed metallurgical mass balance (seed.P1 #258); the
// seed never carries a tails tonne or a tails assay as a literal. Two
// separate quantities live on TailsLossRead — a loss FRACTION (of feed
// metal) and a tails ASSAY (a grade) — and only the assay is ever toned
// against the FMEA V8 spec threshold (brief hard rule 1: one function per
// quantity, never a function that takes a unit string). Pure — no React.
// ---------------------------------------------------------------------------

const HEAD_GRADE_READING_ID: Record<ChainElement, string> = {
  Zn: 'rd-head-zn',
  Pb: 'rd-head-pb',
  Ag: 'rd-head-ag',
};

const RECOVERY_READING_ID: Record<ChainElement, string> = {
  Zn: 'rd-kpi-zn-rec',
  Pb: 'rd-kpi-pb-rec',
  Ag: 'rd-kpi-ag-rec',
};

const CONC_GRADE_READING_ID: Record<'Zn' | 'Pb', string> = {
  Zn: 'rd-spec-zn-grade',
  Pb: 'rd-spec-pb-grade',
};

const TAILS_SPEC_READING_ID: Record<'Zn' | 'Pb' | 'Ag', string> = {
  Zn: 'rd-spec-tails-zn',
  Pb: 'rd-spec-tails-pb',
  Ag: 'rd-spec-tails-ag',
};

const TAILS_LOSS_LABEL: Record<ChainElement, string> = { Zn: 'Zn loss', Pb: 'Pb loss', Ag: 'Ag loss' };

const TAILS_LOSS_PROVENANCE = 'Derived from PeriodBalance — tails metal ÷ feed metal (R11)';

type AssayPrecision = {
  znConcPct: number;
  pbConcPct: number;
  agConcOzPerT: number;
  tailsZnPct: number;
  tailsPbPct: number;
  tailsAgOzPerT: number;
};

function assayPrecisions(): AssayPrecision {
  const sa03 = MEASUREMENTS.find((entry) => entry.id === 'ms-sa03');
  const sa04 = MEASUREMENTS.find((entry) => entry.id === 'ms-sa04');
  const sa05 = MEASUREMENTS.find((entry) => entry.id === 'ms-sa05');
  const p03 = sa03?.precision;
  const p04 = sa04?.precision;
  const p05 = sa05?.precision;
  return {
    pbConcPct: p03?.pbPct ?? 0,
    agConcOzPerT: p03?.agOzPerT ?? 0,
    znConcPct: p04?.znPct ?? 0,
    tailsPbPct: p05?.pbPct ?? 0,
    tailsZnPct: p05?.znPct ?? 0,
    tailsAgOzPerT: p05?.agOzPerT ?? 0,
  };
}

function recoveryPctWithAssays(
  element: ChainElement,
  view: PeriodView,
  override: Partial<{
    pbConcPct: number;
    agConcOzPerT: number;
    znConcPct: number;
    tailsPbPct: number;
    tailsZnPct: number;
    tailsAgOzPerT: number;
  }>,
): number | null {
  if (!isPeriodMeasured(view)) return null;
  const masses = view.masses;
  const base = {
    pbConcPct: view.productGrade.pbConcPct,
    // F765 (#765): the assay basis is the glass's reported grade; this
    // recovery-uncertainty propagation is the balance's product leg, so it reads
    // the closure's back-calculated Ag grade and never moves with the assay.
    agConcOzPerT: view.closureAgConcOzPerT,
    znConcPct: view.productGrade.znConcPct,
    tailsPbPct: view.tailsAssay.tailsPbPct,
    tailsZnPct: view.tailsAssay.tailsZnPct,
    tailsAgOzPerT: view.tailsAssay.tailsAgOzPerT,
  };
  const assays = { ...base, ...override };
  const { C1, C2, T } = masses;
  if (element === 'Pb') {
    const znPb = view.productGrade.znPbInZnConcPct;
    const metalInFeed =
      C1 * (assays.pbConcPct / 100) + C2 * (znPb / 100) + T * (assays.tailsPbPct / 100);
    return (C1 * (assays.pbConcPct / 100) / metalInFeed) * 100;
  }
  if (element === 'Zn') {
    const metalInFeed = C2 * (assays.znConcPct / 100) + T * (assays.tailsZnPct / 100);
    return (C2 * (assays.znConcPct / 100) / metalInFeed) * 100;
  }
  const metalInFeed = C1 * assays.agConcOzPerT + T * assays.tailsAgOzPerT;
  return (C1 * assays.agConcOzPerT / metalInFeed) * 100;
}

/** Recovery ± from assay precisions — three-product propagation (paul-tarp.A16). */
export function recoveryUncertainty(
  element: ChainElement,
  view: PeriodView,
): { margin: number; tooltip: string } | null {
  if (view.period === 'shift' || !isPeriodMeasured(view)) return null;
  const center = recoveryPctWithAssays(element, view, {});
  if (center === null || !Number.isFinite(center)) return null;
  const base = {
    pbConcPct: view.productGrade.pbConcPct,
    // F765 (#765): the closure's back-calculated Ag grade, as above.
    agConcOzPerT: view.closureAgConcOzPerT,
    znConcPct: view.productGrade.znConcPct,
    tailsPbPct: view.tailsAssay.tailsPbPct,
    tailsZnPct: view.tailsAssay.tailsZnPct,
    tailsAgOzPerT: view.tailsAssay.tailsAgOzPerT,
  };
  const prec = assayPrecisions();
  const deltas: number[] = [];
  if (element === 'Zn') {
    deltas.push(
      Math.abs(recoveryPctWithAssays(element, view, { znConcPct: base.znConcPct + prec.znConcPct })! - center),
      Math.abs(recoveryPctWithAssays(element, view, { tailsZnPct: base.tailsZnPct + prec.tailsZnPct })! - center),
    );
  } else if (element === 'Pb') {
    deltas.push(
      Math.abs(recoveryPctWithAssays(element, view, { pbConcPct: base.pbConcPct + prec.pbConcPct })! - center),
      Math.abs(recoveryPctWithAssays(element, view, { tailsPbPct: base.tailsPbPct + prec.tailsPbPct })! - center),
    );
  } else {
    deltas.push(
      Math.abs(recoveryPctWithAssays(element, view, { agConcOzPerT: base.agConcOzPerT + prec.agConcOzPerT })! - center),
      Math.abs(recoveryPctWithAssays(element, view, { tailsAgOzPerT: base.tailsAgOzPerT + prec.tailsAgOzPerT })! - center),
    );
  }
  const margin = Math.sqrt(deltas.reduce((sum, d) => sum + d * d, 0));
  const tp = threeProductRead(element, view);
  const assayNote =
    element === 'Zn'
      ? `SA04 ±${prec.znConcPct}% Zn · SA05 ±${prec.tailsZnPct}% Zn tails [syn]`
      : element === 'Pb'
        ? `SA03 ±${prec.pbConcPct}% Pb · SA05 ±${prec.tailsPbPct}% Pb tails [syn]`
        : `SA03 ±${prec.agConcOzPerT} oz/t Ag · SA05 ±${prec.tailsAgOzPerT} oz/t Ag tails [syn]`;
  return {
    margin: Math.round(margin * 10) / 10,
    tooltip: `${tp.formula} · ${tp.assayIds} · recovery ± ${Math.round(margin * 10) / 10} % · ${assayNote}`,
  };
}

/** Mine ↔ mill grade gap sentence for day close (paul-tarp.A17). */
export function mineMillGapSentence(view: PeriodView, element: ChainElement): string | null {
  if (view.period !== 'day') return null;
  const read = chainRead(view, element);
  const gap = read.gaps[0];
  if (!gap || gap.pct === null || Math.abs(gap.pct) < 0.05) return null;
  const direction =
    gap.pct < 0 ? `carried to the mine's delivered grade` : `carried from the mine's delivered grade`;
  return `${formatSignedGapPct(gap.pct)} contained-metal gap ${direction}`;
}

export function mineMillGapSentences(view: PeriodView): readonly string[] {
  return (['Zn', 'Pb', 'Ag'] as const)
    .map((element) => mineMillGapSentence(view, element))
    .filter((line): line is string => Boolean(line));
}

function recoveredProductMetal(balance: PeriodBalance, element: 'Pb' | 'Zn'): number {
  if (element === 'Pb') {
    return balance.masses.C1 * (balance.productGrade.pbConcPct / 100);
  }
  return balance.masses.C2 * (balance.productGrade.znConcPct / 100);
}

/** Mass-balance closure gap from displayed table figures (R9). */
function balanceClosureGapPct(element: 'Pb' | 'Zn', view: PeriodView): number | null {
  if (!isPeriodMeasured(view)) return null;
  return displayedClosureGapPct(view, element);
}

/** Tone for a tails assay against its spec threshold. Percent-grade elements
 *  (Pb, Zn) compare assayPct to spec.hi; Ag compares oz/t assay to spec.hi. */
function tailsAssayTone(assayValue: number, element: 'Zn' | 'Pb' | 'Ag'): ClosureTone {
  const spec = readingByIdLookup(TAILS_SPEC_READING_ID[element]);
  if (spec?.spec?.hi === undefined) return 'unmeasured';
  return assayValue > spec.spec.hi ? 'bad' : 'ok';
}

/** Orchestrator round 2, ruling G: Paul's V8 sheet states both a Zn recovery
 *  target (`rd-kpi-zn-rec.design`, the PFS design basis) and a Zn
 *  tails-assay target (`rd-spec-tails-zn.spec.hi`) — at the PFS head grade
 *  these cannot both hold. Solved backwards from the same two-product
 *  balance `tailsStreamMassTd`/`concentrateMassTd` use, entirely on
 *  `.design` readings (never `.value`), so the finding is a property of the
 *  PFS design itself, not today's actual conditions:
 *
 *  - `impliedAssayAtDesignPct` — the tails assay a plant running exactly to
 *    its own design recovery would show.
 *  - `impliedRecoveryAtTargetPct` — the recovery a plant would need to
 *    exactly hit the V8 tails-assay ceiling.
 *
 *  Reproduces the worked table in `reconciliation.md` § Tails losses
 *  "Finding"; see that section for the algebra in prose. Zn only, by scope
 *  — this function is not generalised to Pb. That is not because the Pb
 *  target agrees with its design recovery: at design (ore 1,800 t, head Pb
 *  2.40%, Pb recovery 88.2%, the same design tails mass this function
 *  computes for Zn) the implied Pb tails assay is 0.32% against a 0.30%
 *  target — 6% over, the same class of contradiction as Zn's, not rounding
 *  (concentrate-spec review SHOULD-FIX, 2026-09-10). Not surfaced on the
 *  glass by this stream; a Pb equivalent of this function is future work,
 *  not a correction that belongs in this comment. Derived, not authored:
 *  moves with the seed if any of these six readings change. */
export type ZnTailsDesignContradiction = {
  designRecoveryPct: number;
  targetAssayPct: number;
  impliedAssayAtDesignPct: number;
  impliedRecoveryAtTargetPct: number;
};

/** seed.P5 / #295, exported for the concentrate-quality-and-losses read
 *  (paul-feedback.A5 / #265): the Zn tails contradiction rendered at its
 *  point of use rather than resolved by seeding a flattering number (brief
 *  hard rule 3). See the function banner above for the derivation. */
export function znDesignBasisTailsContradiction(): ZnTailsDesignContradiction | null {
  const oreDesign = readingByIdLookup('rd-kpi-ore-td')?.design;
  const headZnDesign = readingByIdLookup(HEAD_GRADE_READING_ID.Zn)?.design;
  const headPbDesign = readingByIdLookup(HEAD_GRADE_READING_ID.Pb)?.design;
  const recZnDesign = readingByIdLookup(RECOVERY_READING_ID.Zn)?.design;
  const recPbDesign = readingByIdLookup(RECOVERY_READING_ID.Pb)?.design;
  const concZnDesign = readingByIdLookup(CONC_GRADE_READING_ID.Zn)?.design;
  const concPbDesign = readingByIdLookup(CONC_GRADE_READING_ID.Pb)?.design;
  const targetAssay = readingByIdLookup(TAILS_SPEC_READING_ID.Zn)?.spec?.hi;
  if (
    oreDesign === undefined ||
    headZnDesign === undefined ||
    headPbDesign === undefined ||
    recZnDesign === undefined ||
    recPbDesign === undefined ||
    concZnDesign === undefined ||
    concPbDesign === undefined ||
    targetAssay === undefined
  ) {
    return null;
  }

  const feedZn = oreDesign * (headZnDesign / 100);
  const feedPb = oreDesign * (headPbDesign / 100);
  const pbConcMass = (feedPb * (recPbDesign / 100)) / (concPbDesign / 100);
  const znConcGradeFrac = concZnDesign / 100;

  const znConcMassAt = (recoveryPct: number) => (feedZn * (recoveryPct / 100)) / znConcGradeFrac;
  const tailsMassAt = (recoveryPct: number) => oreDesign - pbConcMass - znConcMassAt(recoveryPct);
  const assayAt = (recoveryPct: number) => {
    const mass = tailsMassAt(recoveryPct);
    if (!Number.isFinite(mass) || mass <= 0) return null;
    return ((feedZn * (1 - recoveryPct / 100)) / mass) * 100;
  };

  const impliedAssayAtDesignPct = assayAt(recZnDesign);

  // Solve r (recovery fraction) from feedZn(1 − r) = a(oreDesign − pbConcMass − feedZn·r/g),
  // a = targetAssay/100, g = znConcGradeFrac.
  const a = targetAssay / 100;
  const numerator = feedZn - a * oreDesign + a * pbConcMass;
  const denominator = feedZn * (1 - a / znConcGradeFrac);
  const impliedRecoveryAtTargetPct = denominator !== 0 ? (numerator / denominator) * 100 : null;

  if (impliedAssayAtDesignPct === null || impliedRecoveryAtTargetPct === null) return null;

  return { designRecoveryPct: recZnDesign, targetAssayPct: targetAssay, impliedAssayAtDesignPct, impliedRecoveryAtTargetPct };
}

/** paul-fmea.A10 / #275 — tails loss = tails metal ÷ feed metal from `PeriodView`
 *  (R11). Rendered for 24 h, WTD and MTD; shift is unmeasured. */
export function tailsLossRead(element: ChainElement, view: PeriodView): TailsLossRead {
  const period = view.period;
  const empty = (state: TailsLossRead['state'], detail = ''): TailsLossRead => ({
    element,
    period,
    state,
    metalInFeed: UNMEASURED,
    metalToTails: null,
    lossPct: null,
    tailsAssay: null,
    tailsAssayTone: 'unmeasured',
    detail,
    provenance: TAILS_LOSS_PROVENANCE,
  });

  if (!isPeriodMeasured(view) || ACCOUNTING_REPORT.period !== 'day') {
    return empty('value');
  }

  const balance = view;
  const feedMetal = balance.metalIn[element];
  const tailsMass = balance.masses.T;
  if (!Number.isFinite(feedMetal) || feedMetal <= 0 || tailsMass <= 0) return empty('value');

  const feedUnit =
    balance.period === 'day'
      ? element === 'Ag'
        ? 'oz/d'
        : 'dst/d'
      : element === 'Ag'
        ? 'oz'
        : 'dst';
  const metalInFeed: MeasuredQuantity = { value: feedMetal, unit: feedUnit };

  let tailsAssay: MeasuredQuantity;
  let metalToTailsValue: number;
  if (element === 'Ag') {
    tailsAssay = { value: balance.tailsAssay.tailsAgOzPerT, unit: 'oz/t Ag' };
    metalToTailsValue = tailsMass * balance.tailsAssay.tailsAgOzPerT;
  } else if (element === 'Pb') {
    tailsAssay = { value: balance.tailsAssay.tailsPbPct, unit: '% Pb' };
    metalToTailsValue = tailsMass * (balance.tailsAssay.tailsPbPct / 100);
  } else {
    tailsAssay = { value: balance.tailsAssay.tailsZnPct, unit: '% Zn' };
    metalToTailsValue = tailsMass * (balance.tailsAssay.tailsZnPct / 100);
  }
  const metalToTails: MeasuredQuantity = { value: metalToTailsValue, unit: feedUnit };
  const lossPct = feedMetal > 0 ? (metalToTailsValue / feedMetal) * 100 : null;
  const tone = tailsAssayTone(tailsAssay.value ?? 0, element);

  return {
    element,
    period: balance.period,
    state: 'value',
    metalInFeed,
    metalToTails,
    lossPct,
    tailsAssay,
    tailsAssayTone: tone,
    detail: '',
    provenance: TAILS_LOSS_PROVENANCE,
  };
}

export function allTailsLossReads(view: PeriodView): TailsLossRead[] {
  return (['Zn', 'Pb', 'Ag'] as const).map((element) => tailsLossRead(element, view));
}

// ---------------------------------------------------------------------------
// paul-fmea.A12 / #277 — the tails-loss drill (docs/logic/metal-accounting/loss-drill.md).
// Read-only projection: no engine, no mutation. See the logic doc's
// § Transformations "Method" for the full reasoning behind the ranking rule.
// ---------------------------------------------------------------------------

/** Which circuit (owning node + its two real stages) each element's drill
 *  covers. Ag has no entry — no drill affordance for Ag (logic doc § Engine
 *  consumed "Failure mode"): the tails-loss cell stays a plain unlinked cell. */
const LOSS_DRILL_CIRCUIT: Record<'Zn' | 'Pb', { circuitId: string; nodeId: string; label: string; stages: readonly StageId[] }> = {
  Pb: { circuitId: 'pb', nodeId: 'sp-pb', label: 'Lead flotation', stages: ['pb-rougher', 'pb-cleaner'] },
  Zn: { circuitId: 'zn', nodeId: 'sp-zn', label: 'Zinc flotation', stages: ['zn-rougher', 'zn-cleaner'] },
};

/** Hand-curated stage ↔ open-case link — same precedent as
 *  `StageLadder.transform.ts`'s own `STAGE_OVERRIDES`: no seeded field ties a
 *  `Case` to a `StageId` directly, so the mapping is documented here instead.
 *  A stage is named "losing the most" when one of its linked cases is still
 *  open (state not `'closed'` / `'rejected'`) — never from a grade-vs-design
 *  gap, which sits at or near zero for both stages at the PFS design basis
 *  and would not discriminate between them. */
const STAGE_RECOVERY_CASE_LINKS: Partial<Record<StageId, readonly string[]>> = {
  'pb-rougher': ['case-lic'],
  'zn-rougher': ['case-air', 'case-cuso4'],
};

const CLOSED_CASE_STATES = new Set(['closed', 'rejected']);

function openRecoveryCaseForStage(stageId: StageId): { id: string; title: string } | null {
  const caseIds = STAGE_RECOVERY_CASE_LINKS[stageId];
  if (!caseIds) return null;
  for (const caseId of caseIds) {
    const found = CASES.find((entry) => entry.id === caseId);
    if (found && !CLOSED_CASE_STATES.has(found.state)) {
      return { id: found.id, title: found.title };
    }
  }
  return null;
}

function readingQuantity(readingId: string, field: 'value' | 'design'): MeasuredQuantity {
  const reading = readingByIdLookup(readingId);
  if (!reading) return UNMEASURED;
  const raw = field === 'value' ? reading.value : reading.design;
  if (raw === undefined) return UNMEASURED;
  return { value: raw, unit: reading.unit };
}

/** paul-fmea.A12 / #277: rougher + cleaner rows for one element's circuit —
 *  each stage's measured concentrate grade against its own PFS design grade
 *  (same reading, same unit either side), plus whichever open recovery-case
 *  link names that stage, if any. Never a metal tonnage — see loss-drill.md
 *  "What this surface is not". */
export function stageLossRows(element: 'Zn' | 'Pb', view: PeriodView): readonly StageLossRow[] {
  const circuit = LOSS_DRILL_CIRCUIT[element];
  const period = view.period;
  return circuit.stages.map((stageId) => {
    const stage = STAGES.find((entry) => entry.id === stageId);
    const openCase = openRecoveryCaseForStage(stageId);
    const outcomeReadingId = stage?.outcomeReadingId ?? '';
    return {
      stageId,
      label: stage?.label ?? stageId,
      nodeId: circuit.nodeId,
      outcomeReadingId,
      outcomeLabel: readingByIdLookup(outcomeReadingId)?.label ?? outcomeReadingId,
      outcomeValue: stageOutcomeValue(outcomeReadingId, period),
      outcomeDesign: readingQuantity(outcomeReadingId, 'design'),
      // Round 2 fix (ruling B / MF-4 remedy part 2): 'bad' only where an
      // open case names the stage; 'unmeasured' — never 'ok' — everywhere
      // else. "No open case" is absence of evidence, not evidence of
      // health; the seed says nothing about the other stage's recovery.
      tone: openCase ? 'bad' : 'unmeasured',
      caseId: openCase?.id ?? null,
      caseTitle: openCase?.title ?? null,
    };
  });
}

/** paul-fmea.A12 / #277: the whole drill for one element's circuit — the two
 *  stage rows, which one is named worst, and the one-line honest caption the
 *  glass renders verbatim (never left implied).
 *
 *  Round 2 (ruling B / MF-4 remedy part 3): `method` is now the fixed,
 *  one-line basis statement the review asked for, read above the table
 *  (`StageLossDrill.tsx`) rather than a per-row sentence read after it — the
 *  reader needs the basis before the rows, not after. */
export function lossDrillRead(element: 'Zn' | 'Pb', view: PeriodView): LossDrillRead {
  const circuit = LOSS_DRILL_CIRCUIT[element];
  const rows = stageLossRows(element, view);
  const named = rows.find((row) => row.tone === 'bad');
  const worst = named ?? rows[0];
  const method = 'No metered stage-level split exists; stages are ranked by open recovery cases.';
  return {
    element,
    period: view.period,
    circuitId: circuit.circuitId,
    circuitLabel: circuit.label,
    nodeId: circuit.nodeId,
    rows,
    worstStageId: worst?.stageId ?? circuit.stages[0],
    method,
  };
}

function formatPct(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)} %`;
}

/** One cell per element, rendered identically on the closure strip and
 *  `MineToShippedCard` (brief: "the chain card and the strip agree").
 *  Headline `value` is the loss fraction and carries `tone: 'neutral'` —
 *  there is no threshold in that quantity to tone it against (hard rule 1).
 *  `sub` carries the tails assay with its own `subTone` from the FMEA
 *  threshold; the flagged Ag cell shows no number at all. */
function tailsLossCell(read: TailsLossRead): MaClosureCell {
  const id = `ma-${read.element.toLowerCase()}-loss`;
  const label = TAILS_LOSS_LABEL[read.element];
  // paul-fmea.A12 / #277: Zn/Pb only — Ag carries no LOSS_DRILL_CIRCUIT entry
  // (no drill affordance; loss-drill.md § Engine consumed "Failure mode").
  const drillElement = read.element === 'Zn' || read.element === 'Pb' ? read.element : undefined;

  if (read.state === 'flagged') {
    const gapMatch = read.detail.match(/(\d+(?:\.\d+)?)% disagreement/);
    const gapSub = gapMatch ? `${gapMatch[1]} % gap` : 'path disagreement';
    return { id, label, value: 'flagged', sub: gapSub, tone: 'bad', title: read.detail };
  }
  if (read.lossPct === null || read.metalToTails === null) {
    // paul-fmea.A12 / #277: unmeasured (shift/mtd — no seed row) carries no
    // drill entry either, same absence as the headline itself (loss-drill.md
    // § UI states "Shift period").
    return { id, label, value: 'unmeasured', sub: '', tone: 'neutral', title: read.provenance };
  }

  const assaySub = read.tailsAssay
    ? `${formatMeasuredQuantity(read.tailsAssay, undefined, { decimals: 2 })} tails`
    : '';
  const assayTitle = read.tailsAssay
    ? ` Tails assay ${formatMeasuredQuantity(read.tailsAssay, undefined, { decimals: read.element === 'Ag' ? 3 : 2 })} vs target — ${
        read.tailsAssayTone === 'bad' ? 'exceeds' : read.tailsAssayTone === 'warn' ? 'approaching' : read.tailsAssayTone === 'ok' ? 'within' : 'no threshold'
      } threshold.`
    : '';

  return {
    id,
    label,
    value: formatPct(read.lossPct),
    sub: assaySub,
    tone: 'neutral',
    subTone: read.tailsAssayTone === 'unmeasured' ? undefined : read.tailsAssayTone === 'bad' ? 'bad' : 'ok',
    title: `${formatMeasuredQuantity(read.metalToTails, read.period, { decimals: 2 })} of ${formatMeasuredQuantity(
      read.metalInFeed,
      read.period,
      { decimals: 2 },
    )} feed metal to tails (${read.provenance}).${assayTitle}`,
    drillElement,
  };
}

/** paul-fmea.A10 / #275: the three tails-loss cells, rendered on the closure
 *  strip on both `/metal-accounting` and `/plant-model?layer=ma` (round 2,
 *  ruling A: `MineToShippedCard` no longer renders these itself — see its
 *  comment block). */
export function tailsLossCells(view: PeriodView): MaClosureCell[] {
  return allTailsLossReads(view).map(tailsLossCell);
}

/** Signed gap label for the chain card/strip — direction matters here (which
 *  checkpoint claims more), unlike the plant-internal formatGapPct magnitude.
 *  K17 round 1 SHOULD: a gap that displays as zero prints `0.0 %`, never
 *  `−0.0 %` (both `0` and `-0`), the same rule `formatGapPct` follows. */
export function formatSignedGapPct(pct: number): string {
  const displayed = displayedGapPct(pct);
  if (displayed === 0) return '0.0 %';
  const sign = displayed > 0 ? '+' : '−';
  return `${sign}${Math.abs(displayed).toFixed(1)} %`;
}

/** Fix round 1 MR-F10: gap = (downstream − upstream) / upstream, so a
 *  negative number means the downstream checkpoint saw less than the
 *  upstream one claimed/accounted. This phrases that in words for the gap
 *  pill's `title` — the sign alone ('−3.9 %') does not say which side is
 *  higher.
 *
 *  Fix round 2 MR-F17: `gap.to` is only ever 'mill' or 'shipped', and
 *  `gap.from` is only ever 'mine' or 'mill-concentrate' (the chain's two legs
 *  run mine → mill and mill concentrate → shipped) — but the round-1 version
 *  built every sentence from the same {label, verb} pair, so a mill→shipped
 *  gap sentence opened "Shipped shipped …" (the checkpoint name and its verb
 *  are the same word). Subject and verb are now looked up separately for the
 *  sentence-initial ("to") side and the trailing ("from") side, so no sentence
 *  repeats a word. */
const CHECKPOINT_SUBJECT_INITIAL: Record<ChainCheckpoint, string> = {
  mine: 'The mine',
  mill: 'The mill',
  'mill-concentrate': 'The mill concentrate',
  shipped: 'Shipped concentrate',
};
const CHECKPOINT_SUBJECT_LOWER: Record<ChainCheckpoint, string> = {
  mine: 'the mine',
  mill: 'the mill',
  'mill-concentrate': 'the mill concentrate',
  shipped: 'the shipment',
};
const CHECKPOINT_VERB: Record<ChainCheckpoint, string> = {
  mine: 'claimed',
  mill: 'accounted',
  'mill-concentrate': 'produced',
  shipped: 'carried',
};

export function gapTitle(gap: ChainGap, element: ChainElement): string {
  // K17 round 3 (#663): the stock leg is not a missing measurement — it carries
  // its own decode of the produced-vs-despatched stock position.
  if (gap.stock) return gap.stock.title;
  if (gap.pct === null) return 'No measurement for one or both checkpoints this period.';
  const abs = Math.abs(gap.pct);
  const toSubject = CHECKPOINT_SUBJECT_INITIAL[gap.to];
  const toVerb = CHECKPOINT_VERB[gap.to];
  const fromSubject = CHECKPOINT_SUBJECT_LOWER[gap.from];
  const fromVerb = CHECKPOINT_VERB[gap.from];
  if (abs < 0.05) {
    return `${toSubject} ${toVerb} the same ${element} as ${fromSubject} ${fromVerb}.`;
  }
  const direction = gap.pct < 0 ? 'less' : 'more';
  return `${toSubject} ${toVerb} ${abs.toFixed(1)} % ${direction} ${element} than ${fromSubject} ${fromVerb}.`;
}

const CHAIN_TONE_RANK: Record<ClosureTone, number> = { bad: 3, warn: 2, ok: 1, unmeasured: 0 };

function worstChainGap(view: PeriodView): { element: ChainElement; gap: ChainGap } | null {
  let worst: { element: ChainElement; gap: ChainGap } | null = null;
  for (const read of allChainReads(view)) {
    for (const gap of read.gaps) {
      if (gap.pct === null) continue;
      const better =
        !worst ||
        CHAIN_TONE_RANK[gap.tone] > CHAIN_TONE_RANK[worst.gap.tone] ||
        (CHAIN_TONE_RANK[gap.tone] === CHAIN_TONE_RANK[worst.gap.tone] &&
          Math.abs(gap.pct) > Math.abs(worst.gap.pct ?? 0));
      if (better) worst = { element: read.element, gap };
    }
  }
  return worst;
}

/** paul-fmea.A13 / #278: with the accounting layer locked on (`/metal-accounting`),
 *  a node shows only name · gap chip · contained metal — the constraint reads
 *  (utilisation bar, bottleneck badge) stay on `/plant-model`, including
 *  `?layer=ma`, the graph level where they belong (brief step 1). Pure — no
 *  React. `ProcessGraph.tsx` calls this only when `maLocked` is true, never
 *  merely because the MA layer is toggled on via the URL.
 *
 *  Round 2 fix (MF-3): nulling only the three constraint fields left the
 *  node's `sub` line untouched, and `SubprocessNode` falls back to `sub`
 *  whenever `maNote` is unset (`ProcessGraph.tsx`'s `data.maNote ? … :
 *  data.sub ? … : null`). `nodeNotes` is populated only at `period ===
 *  'day'`, so at `?period=mtd` every locked node was reverting to its
 *  `/plant-model` constraint sub-line — the exact annotation this function
 *  exists to suppress. `sub` is nulled here too, at every period. */
export function suppressConstraintReadsOnNode<
  T extends {
    utilizationPct: number | null;
    isBottleneck: boolean;
    bottleneckLabel: string | null;
    sub: string | null;
  },
>(node: T): T {
  return { ...node, utilizationPct: null, isBottleneck: false, bottleneckLabel: null, sub: null };
}

// ---------------------------------------------------------------------------
// vision.V10 / emails.A1–A4 / seed.P4 — in-circuit inventory, decode per stream,
// three-product derivation, period close. Pure — no React.
// ---------------------------------------------------------------------------

export type InCircuitHoldup = {
  subprocessId: string;
  label: string;
  holdupText: string;
  containedText: string;
  decode: MeasurementDecode;
  provenance: string;
};

const STAGE_SOLIDS_FLOW_STPH: Record<string, number> = {
  'sp-grind': 261,
  'sp-pb': 72,
  'sp-zn': 132,
  'sp-filter': 7,
};

export type InCircuitInventoryRow = {
  subprocessId: string;
  solidsTonnes: number;
  dstPb: number | null;
  dstZn: number | null;
  ozAg: number | null;
  gradeLabel: string;
  provenance: string;
};

/** Pure holdup derivation — residence × stage solids flow × named stream grade. */
export function inCircuitInventory(): readonly InCircuitInventoryRow[] {
  const headZn = readingByIdLookup('rd-head-zn')?.value ?? 5.44;
  const headPb = readingByIdLookup('rd-head-pb')?.value ?? 2.4;
  const headAg = readingByIdLookup('rd-head-ag')?.value ?? 0.416;

  return METAL_INVENTORY.map((row) => {
    const flow = STAGE_SOLIDS_FLOW_STPH[row.subprocessId] ?? 0;
    const solids = (row.residenceMinutes / 60) * flow;
    let dstPb: number | null = null;
    let dstZn: number | null = null;
    let ozAg: number | null = null;
    let gradeLabel = 'head grade';

    if (row.subprocessId === 'sp-grind') {
      dstPb = solids * (headPb / 100);
      dstZn = solids * (headZn / 100);
      ozAg = solids * headAg;
      gradeLabel = 'head grade';
    } else if (row.subprocessId === 'sp-pb') {
      dstPb = solids * 0.381;
      ozAg = solids * 12.4;
      gradeLabel = 'Pb rougher conc grade';
    } else if (row.subprocessId === 'sp-zn') {
      dstZn = solids * 0.061;
      gradeLabel = 'Zn circuit feed grade';
    } else if (row.subprocessId === 'sp-filter') {
      dstPb = solids * 0.191;
      dstZn = solids * 0.419;
      // K20 (#717): the blended concentrate filter holdup reads the live
      // Pb/Ag concentrate Ag grade (the balance's product grade, 12.14 oz/t),
      // not the deck's old 11.3.
      ozAg = solids * 12.14;
      gradeLabel = 'blended concentrate grade';
    }

    return {
      subprocessId: row.subprocessId,
      solidsTonnes: solids,
      dstPb,
      dstZn,
      ozAg,
      gradeLabel,
      provenance: `${row.provenance} · ${gradeLabel}`,
    };
  });
}

function formatHoldupLine(row: InCircuitInventoryRow): { tonnage: string; contained: string } {
  const solids = `${Math.round(row.solidsTonnes)} dst`;
  const parts: string[] = [];
  if (row.dstPb !== null) parts.push(`${row.dstPb.toFixed(1)} dst Pb`);
  if (row.dstZn !== null) parts.push(`${row.dstZn.toFixed(1)} dst Zn`);
  if (row.ozAg !== null) parts.push(`${Math.round(row.ozAg)} oz Ag`);
  return { tonnage: solids, contained: parts.join(' · ') };
}

export function inCircuitHoldups(): readonly InCircuitHoldup[] {
  return inCircuitInventory().map((row, index) => {
    const inv = METAL_INVENTORY[index]!;
    const { tonnage, contained } = formatHoldupLine(row);
    return {
      subprocessId: row.subprocessId,
      label: inv.note.split(' — ')[0] ?? row.subprocessId,
      holdupText: tonnage,
      containedText: contained,
      decode: 'back-calculated' as const,
      provenance: row.provenance,
    };
  });
}

export function totalInCircuitHoldup(): { solids: number; label: string } {
  const solids = inCircuitInventory().reduce((sum, row) => sum + row.solidsTonnes, 0);
  return { solids, label: `${Math.round(solids).toLocaleString('en-US')} dst` };
}

export type ThreeProductRead = {
  element: ChainElement;
  period: AccountingPeriod;
  feedMass: MeasuredQuantity;
  feedGrade: MeasuredQuantity;
  recovery: MeasuredQuantity;
  recoveryPct: number | null;
  feedGradePct: number | null;
  formula: string;
  assayIds: string;
};

/** Thin view of `PeriodView` — feed mass, head grade and recovery. */
export function threeProductRead(element: ChainElement, view: PeriodView): ThreeProductRead {
  const period = view.period;
  const balance = isPeriodMeasured(view) ? view : null;

  const feedMass: MeasuredQuantity =
    balance !== null
      ? { value: balance.feed, unit: period === 'day' ? 'dst/d' : 'dst' }
      : UNMEASURED;

  const feedGradePct = balance !== null ? balance.headGrade[element] : null;
  const recoveryPct = balance !== null ? balance.recovery[element] : null;

  const headUnit = element === 'Ag' ? 'oz/t Ag' : `% ${element}`;
  const feedGrade: MeasuredQuantity =
    feedGradePct !== null && Number.isFinite(feedGradePct)
      ? { value: feedGradePct, unit: headUnit }
      : UNMEASURED;
  const recovery: MeasuredQuantity =
    recoveryPct !== null && Number.isFinite(recoveryPct)
      ? { value: recoveryPct, unit: '%' }
      : UNMEASURED;

  return {
    element,
    period,
    feedMass,
    feedGrade,
    recovery,
    recoveryPct: Number.isFinite(recoveryPct ?? NaN) ? recoveryPct : null,
    feedGradePct: Number.isFinite(feedGradePct ?? NaN) ? feedGradePct : null,
    formula: 'F·f = C₁c₁ + C₂c₂ + T·t · recovery = product metal ÷ feed metal',
    assayIds: 'ms-sa03 · ms-sa04 · ms-sa05 · ms-wit021',
  };
}

// ---------------------------------------------------------------------------
// glass-poc-run A3 / #448 — Paul's metal-balance layout (module02_metal_
// balance.html), our numbers. decisions.md "recovery basis: the grounded
// series, not Paul's illustrative figures" (2026-09-17 15:50): every figure
// below reads the existing series/Reading seed, never Paul's typed numbers —
// his page binds the regions (KPI row, mass balance table, recovery table),
// not the values. Pure — no React.
// ---------------------------------------------------------------------------

/** Actual recovery for the period from `PeriodView`. `null` at `shift`. */
export function recoveryActualPct(element: ChainElement, view: PeriodView): number | null {
  if (!isPeriodMeasured(view)) return null;
  return view.recovery[element];
}

/** Budget-basis target and PFS design recovery — constant `Reading` fields,
 *  the same two figures `rd-kpi-*-rec` already carries; not re-derived per
 *  period (a target does not move with the clock). */
export function recoveryTargetPct(element: ChainElement): number | null {
  return readingByIdLookup(RECOVERY_READING_ID[element])?.target ?? null;
}

export function recoveryDesignPct(element: ChainElement): number | null {
  return readingByIdLookup(RECOVERY_READING_ID[element])?.design ?? null;
}

/** Feed tonnage for the period from `PeriodView`. `unmeasured` at shift. */
export function feedTonnageForPeriod(view: PeriodView): MeasuredQuantity {
  if (!isPeriodMeasured(view)) return UNMEASURED;
  return view.period === 'day'
    ? { value: view.feed, unit: 'dst/d' }
    : { value: view.feed, unit: 'dst' };
}

/** Mass-balance closure gap for the KPI card, table closure row, insight and
 *  strip — one figure from the displayed-precision table arithmetic (R9). */
export function closureGapPct(nodeId: 'sp-zn' | 'sp-pb', view: PeriodView): number | null {
  const element = nodeId === 'sp-zn' ? 'Zn' : 'Pb';
  return balanceClosureGapPct(element, view);
}

/** Window label for the honest partial-week/partial-month read (rule 3: "if
 *  the series has fewer days, WTD reads over the days it has and the period
 *  label says so"). Both windows are D25's complete production days, named by
 *  the window itself rather than the cursor's day-of-week or day-of-month.
 *
 *  K17 round 1 (#663): MTD names the complete-production-day window it reads
 *  (D25) — `month to date — 1–7 Jun` — rather than the cursor's day of month
 *  (`day 8 of 30`), which counted the open 8 Jun day that no MTD figure uses.
 *
 *  K17 round 3 (#663): WTD names its own complete-production-day window the
 *  same way — `week to date — 2–7 Jun` — rather than `day 7 of 7`, which
 *  counted the open 8 Jun day. */
export function periodWindowLabel(view: PeriodView): string {
  const period = view.period;
  if (period === 'wtd') {
    const { days } = wtdWindow();
    if (days.length === 0) return 'week to date — no complete production days';
    const first = days[0]!.productionDate;
    const last = days[days.length - 1]!.productionDate;
    return `week to date — ${productionDateLabel(first, last)}`;
  }
  if (period === 'mtd') {
    const { days } = mtdWindow();
    if (days.length === 0) return 'month to date — no complete production days';
    const first = days[0]!.productionDate;
    const last = days[days.length - 1]!.productionDate;
    return `month to date — ${productionDateLabel(first, last)}`;
  }
  if (period === 'shift') return 'this shift';
  return 'last 24 h';
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `25–31 May` / `30 Jun–2 Jul` / `7 Jun` for two production-day dates. */
function productionDateLabel(from: string, to: string): string {
  const [, fromMonth, fromDay] = from.split('-').map(Number);
  const [, toMonth, toDay] = to.split('-').map(Number);
  const fromLabel = `${fromDay} ${MONTH_ABBR[(fromMonth ?? 1) - 1]}`;
  const toLabel = `${toDay} ${MONTH_ABBR[(toMonth ?? 1) - 1]}`;
  if (from === to) return toLabel;
  return fromMonth === toMonth ? `${fromDay}–${toLabel}` : `${fromLabel}–${toLabel}`;
}

export type MassBalanceCheck = { label: string; tone: VizTone } | null;

export type MassBalanceRow = {
  id: string;
  stream: string;
  indent: boolean;
  tonnes: string;
  pbPct: string;
  znPct: string;
  agOzPerT: string;
  check: MassBalanceCheck;
  title: string;
};

function fmtTonnes(value: number | null, decimals = 1): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return `${value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} dst`;
}

function fmtGrade(quantity: MeasuredQuantity, decimals = 2): string {
  if (quantity.value === null) return '—';
  return quantity.value.toFixed(decimals);
}

function concentrateGradeSpecCheck(readingId: string, actualGrade: number): MassBalanceCheck | null {
  for (const product of CONCENTRATE_SPECS) {
    const gradeSpec = product.gradeSpecs.find((row) => row.readingId === readingId);
    if (!gradeSpec || !('lo' in gradeSpec) || gradeSpec.lo === undefined) continue;
    return actualGrade >= gradeSpec.lo
      ? { label: 'Spec ✓', tone: 'ok' }
      : { label: 'Spec ✗', tone: 'bad' };
  }
  return null;
}

/** Zn-in-Pb-conc / Ag-in-Zn-conc — assays the seed has never carried
 *  (`docs/logic/metal-accounting/reconciliation.md` only models SA03/04/05's
 *  own metal); rather than invent a cross-assay, the cell reads honest
 *  absence (rule 1). */
const NO_CROSS_ASSAY = '—';

/** Paul's five-row mass balance table (Feed → Pb/Ag concentrate → Zn
 *  concentrate → Final tailings → Reconciliation closure), our numbers: feed
 *  and concentrate/tailings masses from `productStreamMasses` (the same
 *  three-product balance `threeProductRead` already uses), grades from
 *  PeriodBalance assays (SA03/04/05), the Check column from concentrate-
 *  spec reads (`rd-spec-zn-pb`, `tailsAssayTone`) — never a typed string. */
const UNMEASURED_MASS_BALANCE_ROWS: readonly MassBalanceRow[] = [
  {
    id: 'mb-feed',
    stream: 'Feed (ROM ore)',
    indent: false,
    tonnes: '—',
    pbPct: '—',
    znPct: '—',
    agOzPerT: '—',
    check: null,
    title: 'No mass-balance reading for this period.',
  },
  {
    id: 'mb-pbcon',
    stream: 'Pb/Ag concentrate',
    indent: true,
    tonnes: '—',
    pbPct: '—',
    znPct: '—',
    agOzPerT: '—',
    check: null,
    title: 'No mass-balance reading for this period.',
  },
  {
    id: 'mb-zncon',
    stream: 'Zn concentrate',
    indent: true,
    tonnes: '—',
    pbPct: '—',
    znPct: '—',
    agOzPerT: '—',
    check: null,
    title: 'No mass-balance reading for this period.',
  },
  {
    id: 'mb-tails',
    stream: 'Final tailings',
    indent: true,
    tonnes: '—',
    pbPct: '—',
    znPct: '—',
    agOzPerT: '—',
    check: null,
    title: 'No mass-balance reading for this period.',
  },
  {
    id: 'mb-closure',
    stream: 'Reconciliation closure',
    indent: false,
    tonnes: '—',
    pbPct: '—',
    znPct: '—',
    agOzPerT: '—',
    check: null,
    title: 'No mass-balance reading for this period.',
  },
];

export function massBalanceRows(view: PeriodView): readonly MassBalanceRow[] {
  if (!isPeriodMeasured(view)) return UNMEASURED_MASS_BALANCE_ROWS;

  const balance = view;
  const pb = threeProductRead('Pb', view);
  const zn = threeProductRead('Zn', view);
  const ag = threeProductRead('Ag', view);

  const feedTonnes = fmtTonnes(roundMass(balance.masses.F));
  const pbConTonnes = fmtTonnes(roundMass(balance.masses.C1));
  const znConTonnes = fmtTonnes(roundMass(balance.masses.C2));
  const tailsTonnes = fmtTonnes(roundMass(balance.masses.T));

  const pbConcGrade = roundGrade(balance.productGrade.pbConcPct);
  const znConcGrade = roundGrade(balance.productGrade.znConcPct);
  const pbConCheck = concentrateGradeSpecCheck('rd-spec-pb-grade', pbConcGrade);
  const znConCheck = concentrateGradeSpecCheck('rd-spec-zn-grade', znConcGrade);

  const tailsAssays = balance.tailsAssay;
  const tailsTone: VizTone =
    tailsAssayTone(tailsAssays.tailsPbPct, 'Pb') === 'bad' || tailsAssayTone(tailsAssays.tailsZnPct, 'Zn') === 'bad'
      ? 'bad'
      : tailsAssayTone(tailsAssays.tailsPbPct, 'Pb') === 'ok' && tailsAssayTone(tailsAssays.tailsZnPct, 'Zn') === 'ok'
        ? 'ok'
        : 'neutral';
  const tailsCheck: MassBalanceCheck =
    tailsTone === 'neutral' ? null : { label: tailsTone === 'ok' ? 'Spec ✓' : 'Spec ✗', tone: tailsTone };

  const znGap = displayedClosureGapPct(balance, 'Zn');
  const pbGap = displayedClosureGapPct(balance, 'Pb');
  // K17 (#663): the Check reads the same threshold function as the tone — a
  // 0.5 % displayed gap closes on both, so the strip's ok wash and the table's
  // Check can never disagree.
  const worstGap = Math.max(Math.abs(znGap), Math.abs(pbGap));
  const closes = closureCloses(worstGap);
  const worstTone = closureToneFromGap(worstGap);
  const closureCheck: MassBalanceCheck = {
    label: closes ? 'Closes' : 'Open',
    tone: worstTone === 'unmeasured' ? 'neutral' : worstTone,
  };

  return [
    {
      id: 'mb-feed',
      stream: 'Feed (ROM ore)',
      indent: false,
      tonnes: feedTonnes,
      pbPct: fmtGrade(pb.feedGrade),
      znPct: fmtGrade(zn.feedGrade),
      agOzPerT: fmtGrade(ag.feedGrade, 2),
      check: null,
      title: 'Metered fresh feed (WIT-021); head grades from ROM assay series.',
    },
    {
      id: 'mb-pbcon',
      stream: 'Pb/Ag concentrate',
      indent: true,
      tonnes: pbConTonnes,
      pbPct: pbConcGrade.toFixed(2),
      znPct: NO_CROSS_ASSAY,
      agOzPerT: roundGrade(balance.productGrade.agConcOzPerT).toFixed(2),
      check: pbConCheck,
      title: 'SA03 lab assay — sampled; Check from rd-spec-pb-grade.',
    },
    {
      id: 'mb-zncon',
      stream: 'Zn concentrate',
      indent: true,
      tonnes: znConTonnes,
      pbPct: roundGrade(balance.productGrade.znPbInZnConcPct).toFixed(2),
      znPct: znConcGrade.toFixed(2),
      agOzPerT: NO_CROSS_ASSAY,
      check: znConCheck,
      title: 'SA04 lab assay — sampled; Check from rd-spec-zn-grade.',
    },
    {
      id: 'mb-tails',
      stream: 'Final tailings',
      indent: true,
      tonnes: tailsTonnes,
      pbPct: roundGrade(tailsAssays.tailsPbPct).toFixed(2),
      znPct: roundGrade(tailsAssays.tailsZnPct).toFixed(2),
      agOzPerT: roundGrade(tailsAssays.tailsAgOzPerT).toFixed(2),
      check: tailsCheck,
      title: 'SA05 lab assay — sampled; toned against rd-spec-tails-pb / rd-spec-tails-zn.',
    },
    {
      id: 'mb-closure',
      stream: 'Reconciliation closure',
      indent: false,
      tonnes: '—',
      pbPct: formatGapPct(pbGap),
      znPct: formatGapPct(znGap),
      agOzPerT: '—',
      check: closureCheck,
      title: 'Mass-balance closure from displayed table figures (products + tails assays).',
    },
  ];
}

export type PeriodClosePanelView = {
  title: string;
  rows: readonly PeriodCloseRow[];
  provenance: string;
};

/** K17 round 2 (#663): the calendar window a month's close counts despatch lots
 *  over. June closes D25's complete production days (1–7 Jun) — the same window
 *  the head × recovery estimate covers — so a lot dated 8 Jun is outside the
 *  close. May is the whole calendar month. Bounds are calendar dates, `[from,
 *  to)`; derived from the same `mtdWindow` the estimate reads. */
export type DespatchWindow = {
  /** Inclusive first calendar day, `YYYY-MM-DD`. */
  from: string;
  /** Exclusive last calendar day — a lot dated on it is outside the window. */
  to: string;
  /** Human label, e.g. `1–7 Jun`. */
  label: string;
};

const MONTH_ABBR_INDEX: Readonly<Record<string, number>> = Object.fromEntries(
  MONTH_ABBR.map((month, index) => [month.toLowerCase(), index + 1]),
);

/** `02 Jun 2026` → `2026-06-02`; null when the seed stamp is not that shape. */
function despatchDateIso(despatchDate: string | undefined): string | null {
  const match = despatchDate?.trim().match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
  if (!match) return null;
  const month = MONTH_ABBR_INDEX[match[2]!.toLowerCase()];
  if (!month) return null;
  return `${match[3]}-${String(month).padStart(2, '0')}-${match[1]!.padStart(2, '0')}`;
}

/** The calendar day after `YYYY-MM-DD` — the exclusive end of a window whose
 *  last counted day is `iso`. */
function nextCalendarDay(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(Date.UTC(year!, month! - 1, day! + 1)).toISOString().slice(0, 10);
}

/** The `[from, to)` calendar-date window covered by a run of production days —
 *  the one constructor the MTD, WTD and (implicitly) May windows share. */
function despatchWindowFromProductionDays(
  days: readonly { productionDate: string }[],
): DespatchWindow | null {
  if (days.length === 0) return null;
  const from = days[0]!.productionDate;
  const last = days[days.length - 1]!.productionDate;
  return { from, to: nextCalendarDay(last), label: productionDateLabel(from, last) };
}

export function despatchWindowFor(closeMonth: string): DespatchWindow | null {
  if (closeMonth === 'may-2026') {
    return {
      from: '2026-05-01',
      to: '2026-06-01',
      label: productionDateLabel('2026-05-01', '2026-05-31'),
    };
  }
  if (closeMonth === 'june-2026-mtd') {
    return despatchWindowFromProductionDays(mtdWindow().days);
  }
  return null;
}

/** K17 round 3 (#663): the WTD close's despatch window — D25's complete
 *  production days inside the plant week (2–7 Jun at the demo cursor), on the
 *  same `[from, to)` calendar-date rule and the same `despatchInWindow` test
 *  the month close uses. The June lots carry the `june-2026-mtd` close month;
 *  the WTD read bounds them by this window, never by the product balance. */
export function wtdDespatchWindow(): DespatchWindow | null {
  return despatchWindowFromProductionDays(wtdWindow().days);
}

/** True when a lot's `despatchDate` falls inside the close's window. A lot with
 *  no date, or one dated outside `[from, to)`, does not count (K17 round 2). */
export function despatchInWindow(despatchDate: string | undefined, window: DespatchWindow): boolean {
  const iso = despatchDateIso(despatchDate);
  return iso !== null && iso >= window.from && iso < window.to;
}

function despatchedContainedFromLots(closeMonth: string, element: ChainElement): number {
  const window = despatchWindowFor(closeMonth);
  const lots = SHIPMENTS.filter(
    (entry) =>
      entry.closeMonth === closeMonth &&
      (!window || despatchInWindow(entry.despatchDate, window)) &&
      entry.grades.some((grade) => grade.element === element),
  );
  let total = 0;
  for (const lot of lots) {
    const mass = parseSeedQuantity(lot.tonnes);
    const gradeEntry = lot.grades.find((grade) => grade.element === element);
    if (mass.value === null || !gradeEntry) continue;
    const grade = parseSeedQuantity(gradeEntry.grade);
    const contained = containedFromMassGrade(mass, grade, element);
    total += contained.value ?? 0;
  }
  return total;
}

/** Thousands-separated fixed-precision number (K17 round 1 SHOULD): the close
 *  table never prints `2422.5 dst Zn` beside `17,945 oz Ag`. */
function fixedNumber(value: number, decimals: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function formatContained(element: ChainElement, value: number): string {
  if (element === 'Ag') return `${fixedNumber(Math.round(value), 0)} oz Ag`;
  return `${fixedNumber(value, element === 'Zn' ? 1 : 0)} dst ${element}`;
}

function formatUnaccountedLoss(element: ChainElement, loss: number, pct: number): string {
  // A true minus on both figures (polish.P9 r2): `pct.toFixed` writes a hyphen,
  // which read `−70.4 dst Zn (-11.0 %)` beside the loss's own minus.
  const sign = loss >= 0 ? '+' : '−';
  const abs = Math.abs(loss);
  const pctText = `${pct >= 0 ? '+' : '−'}${Math.abs(pct).toFixed(1)} %`;
  if (element === 'Ag') {
    return `${sign}${fixedNumber(Math.round(abs), 0)} oz Ag (${pctText})`;
  }
  return `${sign}${fixedNumber(abs, element === 'Zn' ? 1 : 0)} dst ${element} (${pctText})`;
}

/** K17 round 1 (#663) item 3: an open month's third column is not a loss — the
 *  concentrate produced but not yet despatched. Unsigned, no tone, and the
 *  percentage names the basis it is a share of. */
function formatNotYetDespatched(element: ChainElement, loss: number, pct: number): string {
  const abs = Math.abs(loss);
  const mass =
    element === 'Ag'
      ? `${fixedNumber(Math.round(abs), 0)} oz Ag`
      : `${fixedNumber(abs, element === 'Zn' ? 1 : 0)} dst ${element}`;
  return `${mass} · ${Math.abs(pct).toFixed(1)} % of produced`;
}

/** The close estimate's measured window as the row's caption, derived from the
 *  estimate's own coverage. A period that closes exactly its measured days —
 *  June on D25's 1–7 Jun window — shows the window alone; a period scaled
 *  beyond the measured days also names the scale (May's 7 measured days → 31). */
function estimateProvenance(coverage: EstimateCoverage): string {
  const span = productionDateLabel(coverage.span.from, coverage.span.to);
  const window = `${span} · ${coverage.measuredDays} measured days`;
  if (coverage.periodDays === coverage.measuredDays) return window;
  return `${window} scaled to ${coverage.periodDays}`;
}

function buildPeriodCloseRow(meta: PeriodCloseRow): PeriodCloseRow {
  // K17 (#663): the estimate covers the whole period this row closes. May runs
  // monthStart(May) → monthStart(June) = 30 Apr 18:00 → 31 May 18:00, 31
  // production days, scaled from its 7 measured days.
  //
  // K17 round 2 (#663) item 1: June closes **D25's window unscaled** — the 7
  // measured complete production days 1–7 Jun (`mtdWindow`) are the period, so
  // the estimate is the flowsheet's own MTD concentrate metal (feed × recovery
  // over the same window) and the page carries one figure for one quantity.
  // Round 1 scaled the 7 measured days to the month's elapsed days (8/7 =
  // 1.1429), which printed 646.9 dst Zn beside the flowsheet's 566.0.
  const june = mtdWindow();
  const isMay = meta.month === 'may-2026';
  const from = isMay ? monthStart('2026-05-01T00:00') : june.from;
  const to = isMay ? june.from : june.to;
  const periodDays = isMay ? daysInMonth('2026-05-01T00:00') : june.days.length;
  const despatched = despatchedContainedFromLots(meta.month, meta.element);
  const { contained: estimate, coverage } = estimateContainedFromSeries(
    from,
    to,
    periodDays,
    meta.element,
  );
  const loss = despatched - estimate;
  const pct = estimate > 0 ? (loss / estimate) * 100 : 0;
  const open = meta.state === 'open';
  return {
    ...meta,
    despatchedContained: formatContained(meta.element, despatched),
    estimateContained: formatContained(meta.element, estimate),
    unaccountedLoss: open
      ? formatNotYetDespatched(meta.element, loss, pct)
      : formatUnaccountedLoss(meta.element, loss, pct),
    unaccountedPct: pct,
    estimateProvenance: estimateProvenance(coverage),
  };
}

export function periodClosePanel(view: PeriodView): PeriodClosePanelView | null {
  if (view.period !== 'mtd') return null;
  const mayRows = PERIOD_CLOSE.filter((row) => row.month === 'may-2026').map(buildPeriodCloseRow);
  const junRows = PERIOD_CLOSE.filter((row) => row.month === 'june-2026-mtd').map(buildPeriodCloseRow);
  return {
    title: 'Period close',
    rows: [...mayRows, ...junRows],
    provenance: '[syn] smelter settlement vs head × recovery · illustrative (emails.A4 #321)',
  };
}

export function metalAccountingLayer(view: PeriodView): MetalAccountingLayerView {
  const measured = isPeriodMeasured(view);
  const balance = measured ? view : null;
  const period = view.period;
  const znRead = closureRead('sp-zn', view);
  const pbRead = closureRead('sp-pb', view);
  const anchorFlag = GROSS_ERROR_FLAGS.find((flag) => flag.id === 'gef-wit021');
  const anchorRef = anchorFlag?.measurementId
    ? MEASUREMENTS.find((entry) => entry.id === anchorFlag.measurementId)?.sourceRef ?? 'WIT-021'
    : 'WIT-021';

  const nodeNotes: Record<string, MaAnnotation> = {};
  if (measured && period === 'day' && ACCOUNTING_REPORT.period === 'day') {
    nodeNotes['sp-crush'] = {
      text: `${anchorRef} sole anchor`,
      tone: 'warn',
      title: `${anchorFlag?.testStatistic ?? ''} (${anchorFlag?.provenance ?? ''})`,
    };
    for (const holdup of inCircuitHoldups()) {
      const inventory = METAL_INVENTORY.find((entry) => entry.subprocessId === holdup.subprocessId);
      nodeNotes[holdup.subprocessId] = {
        text: holdup.holdupText,
        subText: holdup.containedText,
        tone: 'neutral',
        title: `${inventory?.metal ?? ''} · ${inventory?.note ?? ''} · back-calculated (${holdup.provenance})`,
      };
    }
  }

  const nodeGaps: Record<string, NodeGapChip | null> = {};
  for (const subprocess of SUBPROCESSES) {
    const read = closureRead(subprocess.id, view);
    nodeGaps[subprocess.id] = gapChip(read.closurePct, read.tone);
  }

  const edgeLabels: Record<string, readonly MaAnnotation[]> = {};
  if (measured && znRead.tone !== 'unmeasured' && znRead.closurePct !== null) {
    const znMetal = recoveredProductMetal(view, 'Zn');
    const znGapDisplayed = displayedClosureGapPct(view, 'Zn');
    const znTone = closureToneFromGap(znGapDisplayed);
    edgeLabels['st-zncon'] = [
      {
        text: `${znMetal.toFixed(1)} dst Zn`,
        tone: znTone === 'bad' ? 'bad' : 'ok',
        title: `Recovered Zn — ${view.masses.C2.toFixed(1)} dst × ${view.productGrade.znConcPct.toFixed(1)} % = ${znMetal.toFixed(1)} dst`,
      },
      {
        text: `✓ ${formatGapPct(znRead.closurePct)}`,
        tone: znTone === 'bad' ? 'bad' : znTone === 'warn' ? 'warn' : 'ok',
        title: `Mass-balance closure gap — ${formatGapPct(znGapDisplayed)}`,
      },
    ];
  }
  if (measured && pbRead.tone !== 'unmeasured' && pbRead.closurePct !== null) {
    const pbMetal = recoveredProductMetal(view, 'Pb');
    const pbGapDisplayed = displayedClosureGapPct(view, 'Pb');
    const pbTone = closureToneFromGap(pbGapDisplayed);
    edgeLabels['st-pbcon'] = [
      {
        text: `${pbMetal.toFixed(1)} dst Pb`,
        tone: pbTone === 'bad' ? 'bad' : 'ok',
        title: `Recovered Pb — ${view.masses.C1.toFixed(1)} dst × ${view.productGrade.pbConcPct.toFixed(1)} % = ${pbMetal.toFixed(1)} dst`,
      },
      {
        text: `✓ ${formatGapPct(pbRead.closurePct)}`,
        tone: pbTone === 'bad' ? 'bad' : pbTone === 'warn' ? 'warn' : 'ok',
        title: `Mass-balance closure gap — ${formatGapPct(pbGapDisplayed)}`,
      },
    ];
  }

  const inCircuitTotal = totalInCircuitHoldup();
  const estimatedCount = METAL_INVENTORY.filter((entry) => entry.status === 'estimated').length;

  const znGap = measured ? displayedClosureGapPct(view, 'Zn') : null;
  const pbGap = measured ? displayedClosureGapPct(view, 'Pb') : null;
  const znRecoveredSub =
    measured && balance ? `${recoveredProductMetal(balance, 'Zn').toFixed(1)} dst Zn` : '';
  const pbRecoveredSub =
    measured && balance ? `${recoveredProductMetal(balance, 'Pb').toFixed(1)} dst Pb` : '';
  const feedRateSub =
    measured && balance && period === 'day'
      ? `${Math.round(balance.feed).toLocaleString('en-US')} dst/d`
      : measured && balance
        ? `${Math.round(balance.feed).toLocaleString('en-US')} dst`
        : '';

  const strip: MaClosureStripView = {
    cells: [
      {
        id: 'ma-zn-gap',
        label: 'Zn gap',
        value: znGap !== null ? formatGapPct(znGap) : GLASS_NO_READING,
        sub: znRecoveredSub,
        tone:
          znGap !== null
            ? closureToneFromGap(znGap) === 'bad'
              ? 'bad'
              : closureToneFromGap(znGap) === 'warn'
                ? 'warn'
                : 'ok'
            : 'neutral',
        title:
          znGap !== null
            ? 'Mass-balance closure — recovery-derived product rows vs feed metal'
            : 'No mass-balance reading for this period.',
      },
      {
        id: 'ma-pb-gap',
        label: 'Pb gap',
        value: pbGap !== null ? formatGapPct(pbGap) : GLASS_NO_READING,
        sub: pbRecoveredSub,
        tone:
          pbGap !== null
            ? closureToneFromGap(pbGap) === 'bad'
              ? 'bad'
              : closureToneFromGap(pbGap) === 'warn'
                ? 'warn'
                : 'ok'
            : 'neutral',
        title:
          pbGap !== null
            ? 'Mass-balance closure — recovery-derived product rows vs feed metal'
            : 'No mass-balance reading for this period.',
      },
      {
        id: 'ma-anchor',
        label: 'Balance anchor',
        value:
          !measured
            ? GLASS_NO_READING
            : period === 'day'
              ? anchorRef
              : period === 'wtd' && balance
                ? `${anchorRef} · ${Math.round(balance.feed).toLocaleString('en-US')} dst`
                : balance
                  ? `${anchorRef} · ${Math.round(balance.feed).toLocaleString('en-US')} dst`
                  : anchorRef,
        sub:
          !measured
            ? 'unmeasured'
            : period === 'day'
              ? `sole anchor · ${feedRateSub}`
              : period === 'wtd'
                ? 'WTD cumulative feed from ore series'
                : period === 'mtd'
                  ? 'MTD cumulative feed from ore series'
                  : 'unmeasured',
        tone: measured && period === 'day' ? 'warn' : 'neutral',
        title: `${anchorFlag?.narrative ?? ''} (${anchorFlag?.provenance ?? ''})`,
      },
      {
        id: 'ma-in-circuit',
        label: 'Metal in circuit',
        value: measured && period === 'day' ? inCircuitTotal.label : GLASS_NO_READING,
        sub:
          measured && period === 'day'
            ? `back-calculated · ${estimatedCount} stages`
            : 'unmeasured',
        tone: 'neutral',
        title: `Residence time × flow across ${estimatedCount} holdup stages · total ${inCircuitTotal.label} · back-calculated (${METAL_INVENTORY[0]?.provenance ?? ''})`,
      },
      ...tailsLossCells(view),
      (() => {
        // The label names the read (both legs); the value carries the worst
        // single leg's element and gap, and the sub names that leg in words.
        // The strip omits this cell on /metal-accounting, where
        // `MineToShippedCard` carries the read; on /plant-model?layer=ma it
        // renders on the strip's caption row beside the deep link.
        const worst = worstChainGap(view);
        return {
          id: 'ma-chain-gap',
          label: 'Mine ↔ shipped',
          value: worst
            ? `${worst.element} ${formatSignedGapPct(worst.gap.pct ?? 0)}`
            : GLASS_NO_READING,
          sub: worst ? `${worst.gap.from} → ${worst.gap.to}` : '',
          tone: worst ? (worst.gap.tone === 'unmeasured' ? 'neutral' : worst.gap.tone) : 'neutral',
          title: worst
            ? gapTitle(worst.gap, worst.element)
            : period === 'mtd'
              ? 'Mine ↔ mill ↔ shipped — mill from the ore balance, shipped from despatched lots to date'
              : 'Mine ↔ mill ↔ shipped reconciliation — day only',
        };
      })(),
    ],
    caption: periodWindowLabel(view),
    provenance: 'M2 §6.1–6.4 mass-balance reconciliation',
    provenanceChip: 'basis',
  };

  return { nodeNotes, nodeGaps, edgeLabels, strip };
}

/** Period-aware MA overlay — keeps ProcessGraph.transform.ts free of period wiring.
 *
 *  One corridor, one mark (r11 SHOULD-FIX 5): `st-pbcon`'s flag sat at x 712,
 *  which is the Zn card's own recirculating arc band — the arc spans flow x
 *  674–730 with its crown at y 62 — and the stroke ran through the digits. The
 *  flag moved right, to the stretch of over-arc that has already risen clear
 *  (y ≈ 33 at x 800) and where no other mark is drawn. `MA_LABEL_BAND_TOP`
 *  below is derived from this table, so moving a flag up grows the canvas that
 *  has to hold it. This table is the single source for MA label anchors —
 *  `ProcessGraph.transform` imports it rather than keeping a second copy that
 *  can drift. */
export const MA_EDGE_LABEL_POS: Record<string, readonly { x: number; y: number; anchor: 'start' | 'middle' | 'end' }[]> = {
  'st-zncon': [
    { x: 792, y: 160, anchor: 'middle' },
    { x: 792, y: 182, anchor: 'middle' },
  ],
  'st-pbcon': [{ x: 800, y: 56, anchor: 'middle' }],
};

export type MaOverlayNode = {
  maNote: MaAnnotation | null;
  maGap: NodeGapChip | null;
};

export function overlayMetalAccountingOnEdges<
  T extends { stream: { id: string }; maLabels: readonly unknown[]; valueLabel: unknown },
>(edges: readonly T[], ma: MetalAccountingLayerView): T[] {
  return edges.map((edge) => {
    const maLines = ma.edgeLabels[edge.stream.id] ?? [];
    const maPos = MA_EDGE_LABEL_POS[edge.stream.id] ?? [];
    const maLabels = maLines.slice(0, maPos.length).map((line, index) => ({
      ...line,
      ...maPos[index]!,
    }));
    return {
      ...edge,
      valueLabel: maLabels.length > 0 ? null : edge.valueLabel,
      maLabels,
    };
  });
}
